import { IconSparkle } from "@assets";
import { GiftBoxIcon } from "@components/data-display";
import BrandLogo from "@components/data-display/BrandLogo";
import ContactButtonGroup from "@components/inputs/ContactButtonGroup";
import { LazyLoadComponent } from "@components/utils/LazyLoadComponent";
import { openOnboardingDialog } from "@components/utils/OnboardingDialog/utils";
import TopBarBadge from "@components/utils/TopBarBadge";
import UserModeRenderer from "@components/utils/UserModeRenderer";
import { AuthSource, useStatsigTracking } from "@configs/statsig";
import { BALANCE_BADGE_TOP_ID, HEADER_HEIGHT, OnboardMode, Paths } from "@constants";
import usePromotionStatus from "@hooks/usePromotionStatus";
import { UserMode } from "@types";
import { Button, PageHeader as KZPageHeader, PropsWithClassName, cn } from "kz-ui-sdk";
import { lazy, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { matchPath, useLocation } from "react-router-dom";
import "./PageHeader.css";

interface PageHeaderProps extends PropsWithClassName {}

const deferredUpdateBalancePaths = [Paths.PRIVATE.PROMOTION_CASHBACK, Paths.PRIVATE.PROMOTION_REFER_FRIEND];
const skipUpdateBalancePaths = [Paths.PRIVATE.PROMOTION_DAILY_LOGIN];

const LazyBalanceBadge = lazy(() => import(/* webpackChunkName: "BalanceBadge" */ "@components/utils/BalanceBadge"));

export default function PageHeader({ className }: PageHeaderProps) {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { trackRegisterClick, trackLoginClick } = useStatsigTracking();
  const { isDailyBonusAvailable } = usePromotionStatus();
  const handleRegisterClick = () => {
    // Track registration intent with specialized function
    trackRegisterClick(AuthSource.PAGE_HEADER_REGISTER_BUTTON);

    openOnboardingDialog({
      mode: OnboardMode.REGISTER,
    });
  };

  const { delayUpdate, skipUpdate } = useMemo(() => {
    let delayUpdate: number | undefined;
    let skipUpdate: boolean | undefined;
    // In daily reward page, skip balance updates only if user is eligible (claiming reward)
    // For ineligible users (depositing), allow balance updates to reflect deposit changes
    const isOnDailyLoginPage = skipUpdateBalancePaths.some((path) => matchPath(path, pathname));
    if (isOnDailyLoginPage && isDailyBonusAvailable) {
      skipUpdate = true;
    }
    // In cashback/refer page, we need to delay the update of balance badge
    if (deferredUpdateBalancePaths.some((path) => matchPath(path, pathname))) {
      delayUpdate = 1200;
    }
    return {
      delayUpdate,
      skipUpdate,
    };
  }, [pathname, isDailyBonusAvailable]);

  return (
    <KZPageHeader
      fixed
      className={cn("z-10", className)}
    >
      <div
        className="mx-auto flex max-w-md flex-row items-center justify-between border-b border-white/10 bg-white/5 bg-header px-3 py-3"
        style={{
          height: HEADER_HEIGHT,
        }}
      >
        {/*LOGO*/}
        <div className="h-8 w-fit min-w-fit px-1.5">
          <BrandLogo
            size="auto"
            navigateTo={Paths.PRIVATE.HOME}
          />
        </div>

        {/*TOP BAR BADGE*/}
        <TopBarBadge className="ml-1" />

        {/*CONTACT LINKS*/}
        <div className="mx-1 ml-auto flex gap-1">{<ContactButtonGroup />}</div>

        {/*MEMBER MODE*/}
        <UserModeRenderer mode={UserMode.MEMBER}>
          {/*BALANCE BADGE*/}
          <LazyLoadComponent>
            <LazyBalanceBadge
              id={BALANCE_BADGE_TOP_ID}
              delayUpdate={delayUpdate}
              skipAutoUpdate={skipUpdate}
            />
          </LazyLoadComponent>
        </UserModeRenderer>

        {/*OPEN MODE*/}
        <UserModeRenderer mode={UserMode.GUEST}>
          {/*LOGIN*/}
          <Button
            size={"sm"}
            variant={"bordered"}
            className="h-[30px] !w-fit !border px-2"
            aria-label="Header login"
            onClick={() => {
              trackLoginClick(AuthSource.PAGE_HEADER_LOGIN_BUTTON);
              openOnboardingDialog({
                mode: OnboardMode.LOGIN,
              });
            }}
          >
            {t("Login")}
          </Button>
          {/*REGISTER*/}
          <span className="relative ml-1 inline-block h-[30px]">
            <Button
              size={"sm"}
              variant={"primary"}
              className="h-[30px] !w-fit px-2"
              shiny
              aria-label="Header register"
              onClick={handleRegisterClick}
            >
              <GiftBoxIcon
                width={16}
                height={16}
                animated
                shadow
                className="-mr-1"
              />
              {t("register")}
            </Button>
            <IconSparkle className="page-header__register-sparkle absolute -right-1.5 -top-1.5" />
          </span>
        </UserModeRenderer>
      </div>
    </KZPageHeader>
  );
}
